import { CONNECTOR_NAME } from './connectors';
import { ROLES } from './Role';
import { SUPPORT_EMAIL } from './Developer';
import { BOT_NAME } from './Bot';
import { DAY_IN_SECONDS } from './time';
import { GOOGLE_SHEETS_ROW_LIMIT } from './sheets';

export const DATA_GRID_MEMORY_SEARCH_DEBOUNCE = 1000; // in ms
export const MEMORY_AUTO_TRACKING_MIN_LIMIT = 1;

export const GOOGLE_SHEET_MIME_TYPE = 'application/vnd.google-apps.spreadsheet';
export const GOOGLE_DOC_MIME_TYPE = 'application/vnd.google-apps.document';
export const GOOGLE_SLIDES_MIME_TYPE =
  'application/vnd.google-apps.presentation';
export const PDF_MIME_TYPE = 'application/pdf';
export const DOCX_MIME_TYPE =
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
export const DOC_MIME_TYPE = 'application/msword';

export const JSON_MIME_TYPE = 'application/json';

export const SUPPORTED_GOOGLE_DRIVE_MIME_TYPES = [
  GOOGLE_SHEET_MIME_TYPE,
  GOOGLE_DOC_MIME_TYPE,
  GOOGLE_SLIDES_MIME_TYPE,
  PDF_MIME_TYPE,
  DOCX_MIME_TYPE,
  DOC_MIME_TYPE,
  JSON_MIME_TYPE,
  'text/plain',
  'text/csv',
];

export const UNSUPPORTED_FILE_TYPE_ERROR_MESSAGE =
  'This file type is not supported. Please select a Google Doc, Sheet, Slide, PDF, DOCX, DOC, text file, or JSON file.';

export enum MEMORY_STATUS {
  STALE = 'STALE',
  ACTIVE = 'ACTIVE',
  FRESH = 'FRESH',
}

export const MEMORY_STATUS_COLORS: Record<
  MEMORY_STATUS,
  'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'
> = {
  ACTIVE: 'info',
  FRESH: 'success',
  STALE: 'error',
};

export const MEMORY_INGESTION_STATUS_COLORS: Record<
  MEMORY_BATCH_INGESTION_STATUS,
  'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'
> = {
  INGESTED: 'success',
  PARTIALLY_INGESTED: 'warning',
  IN_PROGRESS: 'warning',
  ERROR: 'error',
  ABORTED: 'error',
};

export const MEMORY_ALLOWED_ROLES: Array<keyof typeof ROLES> = [
  ROLES.SUPER_ADMIN,
  ROLES.ADMIN,
  ROLES.DOMAIN_OWNER,
];

export const GLOBAL_MEMORY_CUD_ACCESS: Array<keyof typeof ROLES> = [
  ROLES.SUPER_ADMIN,
];

export enum MEMORY_LEVELS {
  JUST_MYSELF = 'JUST_MYSELF',
  ORG = 'ORG',
}

export const MEMORY_LEVEL_OPTIONS = [
  { value: MEMORY_LEVELS.JUST_MYSELF, label: MEMORY_LEVELS.JUST_MYSELF },
  { value: MEMORY_LEVELS.ORG, label: MEMORY_LEVELS.ORG },
];

export enum MEMORY_BATCH_TYPES {
  TEXT_MEMORY = 'TEXT_MEMORY',
  RECURSIVE_URL_MEMORY = 'RECURSIVE_URL_MEMORY',
  GOOGLE_DOCS_MEMORY = 'GOOGLE_DOCS_MEMORY',
  GOOGLE_DRIVE_MEMORY = 'GOOGLE_DRIVE_MEMORY',
  PDF_MEMORY = 'PDF_MEMORY',
  DOC_DOCX_MEMORY = 'DOC_DOCX_MEMORY',
  AUDIO_MEMORY = 'AUDIO_MEMORY',
  IMAGE_MEMORY = 'IMAGE_MEMORY',
  YOUTUBE_CAPTIONS_MEMORY = 'YOUTUBE_CAPTIONS_MEMORY',
  CONFLUENCE_MEMORY = 'CONFLUENCE_MEMORY',
  JIRA_MEMORY = 'JIRA_MEMORY',
  GOOGLE_CHAT_SPACE_MEMORY = 'GOOGLE_CHAT_SPACE_MEMORY',
  GOOGLE_CHAT_SPACE_AUTO_TRACKING = 'GOOGLE_CHAT_SPACE_AUTO_TRACKING',
  GOOGLE_MEET_TRANSCRIPT_MEMORY = 'GOOGLE_MEET_TRANSCRIPT_MEMORY',
  GOOGLE_MEET_TRANSCRIPT_AUTO_TRACKING = 'GOOGLE_MEET_TRANSCRIPT_AUTO_TRACKING',
  GOOGLE_TASKS_AUTO_TRACKING = 'GOOGLE_TASKS_AUTO_TRACKING',
  GMAIL_INBOX_LABEL_MEMORY = 'GMAIL_INBOX_LABEL_MEMORY',
  GMAIL_INBOX_LABEL_AUTO_TRACKING = 'GMAIL_INBOX_LABEL_AUTO_TRACKING',
}

export const MEMORY_DYNAMIC_SYNC_STALE_DAYS = 28;

export const DYNAMIC_MEMORY_BATCH_TYPES: Array<MEMORY_BATCH_TYPES> = [
  MEMORY_BATCH_TYPES.RECURSIVE_URL_MEMORY,
  MEMORY_BATCH_TYPES.GOOGLE_DOCS_MEMORY,
  MEMORY_BATCH_TYPES.GOOGLE_DRIVE_MEMORY,
  MEMORY_BATCH_TYPES.YOUTUBE_CAPTIONS_MEMORY,
  MEMORY_BATCH_TYPES.CONFLUENCE_MEMORY,
  MEMORY_BATCH_TYPES.JIRA_MEMORY,
  MEMORY_BATCH_TYPES.GOOGLE_CHAT_SPACE_MEMORY,
  MEMORY_BATCH_TYPES.GOOGLE_CHAT_SPACE_AUTO_TRACKING,
  MEMORY_BATCH_TYPES.GOOGLE_MEET_TRANSCRIPT_MEMORY,
  MEMORY_BATCH_TYPES.GOOGLE_MEET_TRANSCRIPT_AUTO_TRACKING,
  MEMORY_BATCH_TYPES.GOOGLE_TASKS_AUTO_TRACKING,
  MEMORY_BATCH_TYPES.GMAIL_INBOX_LABEL_MEMORY,
  MEMORY_BATCH_TYPES.GMAIL_INBOX_LABEL_AUTO_TRACKING,
];

export const MEMORY_DYNAMIC_SOURCE_LABELS: Record<MEMORY_BATCH_TYPES, string> =
  {
    [MEMORY_BATCH_TYPES.RECURSIVE_URL_MEMORY]: 'Website URLs',
    [MEMORY_BATCH_TYPES.GOOGLE_DOCS_MEMORY]: 'Google Docs',
    [MEMORY_BATCH_TYPES.GOOGLE_DRIVE_MEMORY]: 'Google Drive',
    [MEMORY_BATCH_TYPES.YOUTUBE_CAPTIONS_MEMORY]: 'YouTube',
    [MEMORY_BATCH_TYPES.CONFLUENCE_MEMORY]: 'Confluence',
    [MEMORY_BATCH_TYPES.JIRA_MEMORY]: 'Jira',
    [MEMORY_BATCH_TYPES.GOOGLE_CHAT_SPACE_MEMORY]: 'Google Chat',
    [MEMORY_BATCH_TYPES.GOOGLE_CHAT_SPACE_AUTO_TRACKING]: 'Google Chat',
    [MEMORY_BATCH_TYPES.GOOGLE_MEET_TRANSCRIPT_MEMORY]: 'Google Meet',
    [MEMORY_BATCH_TYPES.GOOGLE_MEET_TRANSCRIPT_AUTO_TRACKING]: 'Google Meet',
    [MEMORY_BATCH_TYPES.GOOGLE_TASKS_AUTO_TRACKING]: 'Google Tasks',
    [MEMORY_BATCH_TYPES.GMAIL_INBOX_LABEL_MEMORY]: 'Gmail',
    [MEMORY_BATCH_TYPES.GMAIL_INBOX_LABEL_AUTO_TRACKING]: 'Gmail',
    [MEMORY_BATCH_TYPES.TEXT_MEMORY]: 'Text',
    [MEMORY_BATCH_TYPES.PDF_MEMORY]: 'PDF',
    [MEMORY_BATCH_TYPES.DOC_DOCX_MEMORY]: 'Docs',
    [MEMORY_BATCH_TYPES.AUDIO_MEMORY]: 'Audio',
    [MEMORY_BATCH_TYPES.IMAGE_MEMORY]: 'Images',
  };

export enum MEMORY_BATCH_INGESTION_STATUS {
  IN_PROGRESS = 'IN_PROGRESS',
  INGESTED = 'INGESTED',
  PARTIALLY_INGESTED = 'PARTIALLY_INGESTED',
  ERROR = 'ERROR',
  ABORTED = 'ABORTED',
}

export const DELETABLE_MEMORY_BATCH_STATUSES: Array<MEMORY_BATCH_INGESTION_STATUS> =
  [
    MEMORY_BATCH_INGESTION_STATUS.INGESTED,
    MEMORY_BATCH_INGESTION_STATUS.ERROR,
    MEMORY_BATCH_INGESTION_STATUS.ABORTED,
  ];

export enum MEMORY_TABS {
  sourceHealth = 'sourceHealth',
  workspaceNotes = 'workspaceNotes',
  generalMemoryList = 'generalMemoryList',
  erroredMemoryBatches = 'erroredMemoryBatches',
  inProgressMemoryBatches = 'inProgressMemoryBatches',
  googleDocMemoryList = 'googleDocMemoryList',
  googleDriveMemoryList = 'googleDriveMemoryList',
  confluenceMemoryList = 'confluenceMemoryList',
  jiraMemoryList = 'jiraMemoryList',
  // automaticGraphMemory = 'automaticGraphMemory',  // TODO: taking auto memory down, need to enable after revamping it
  recursiveUrlMemory = 'recursiveUrlMemory',
  pdfMemory = 'pdfMemory',
  docDocxMemory = 'docDocxMemory',
  audioMemoryList = 'audioMemoryList',
  imageMemoryList = 'imageMemoryList',
  googleChatSpaceMemoryList = 'googleChatSpaceMemoryList',
  youtubeCaptionsMemoryList = 'youtubeCaptionsMemoryList',
  gmailMemoryList = 'gmailMemoryList',
}

export const MEMORY_TAB_QUERY_PARAM = 'tab';
export const MEMORY_BATCH_DETAILS_SECTION_QUERY_PARAM = 'section';

export const SUPER_ADMIN_ONLY_MEMORY_TABS: ReadonlyArray<MEMORY_TABS> = [
  MEMORY_TABS.erroredMemoryBatches,
  MEMORY_TABS.inProgressMemoryBatches,
];

export const INGESTION_STARTED_TEXT =
  'Ingestion has started. Please note that this might take a few minutes to process. Keep checking the ingestion status from time to time.';

export const GLOBAL_MEMORY_TOOLTIP_TEXT =
  'These are Hana system default memories available to all users of Hana. Why this matters: global memories shape answers for everyone, so keep them broadly applicable.';

export const MEMORY_TAGS_INFO_TITLE = 'About memory tags';
export const MEMORY_TAGS_INFO_MARKDOWN = `
## What tags are

Tags are labels you attach to memory batches so related content can be grouped in a way that matches how your team thinks about information.

## What happens when you click or select tags here

Using the tags filter on the memory screen narrows the list to memory batches that match the selected tags. It helps you find the right set of memories faster when the list gets large.

## What tags do

- Organize memory batches by topic, owner, workflow, customer, product area, or any other internal grouping you use.
- Make filtering and review easier across the memory module.
- Help teams create a shared structure for browsing and maintaining memories over time.

## What tags do not do

- Tags do not change the source content of a memory batch.
- Tags do not re-ingest, rewrite, or delete memories.
- Tags do not change access permissions by themselves.
- Tags do not automatically affect memory quality unless someone uses them to filter, review, or manage batches.

## How tags get added

Tags are assigned to memory batches when someone selects them during memory management or later updates the batch metadata. A single memory batch can have multiple tags if it belongs to more than one category.

## Examples

- Use \`sales\`, \`support\`, or \`engineering\` to separate operational knowledge.
- Use \`customer-acme\` or \`customer-beta\` to track customer-specific memory batches.
- Use \`policy\`, \`playbook\`, or \`faq\` to distinguish the type of knowledge stored.
- Use \`needs-review\` to mark batches that should be checked again later.

## When tags are useful

Tags are most helpful when your team needs a consistent way to review, audit, clean up, or locate memory batches across many sources.
`;

export const MEMORY_BATCH_TYPES_INFO_TITLE = 'About memory batch types';
const MEMORY_BATCH_TYPE_INFO_SECTIONS: ReadonlyArray<{
  type: MEMORY_BATCH_TYPES;
  heading: string;
  description: string;
}> = [
  {
    type: MEMORY_BATCH_TYPES.TEXT_MEMORY,
    heading: 'Text memory',
    description:
      'Plain text entered directly into Hana. Use this for short, curated facts, rules, notes, or instructions that do not need a file or external connector.',
  },
  {
    type: MEMORY_BATCH_TYPES.RECURSIVE_URL_MEMORY,
    heading: 'Website URL memory',
    description:
      'Content ingested from a website or a recursive set of linked pages. Use this when knowledge lives on public or internal webpages and should be pulled in from the source URL structure instead of being copied manually.',
  },
  {
    type: MEMORY_BATCH_TYPES.GOOGLE_DOCS_MEMORY,
    heading: 'Google Docs memory',
    description:
      'A Google Doc ingested as memory. Use this for documents that are collaboratively edited in Google Docs and should stay tied to that document as the source of truth.',
  },
  {
    type: MEMORY_BATCH_TYPES.GOOGLE_DRIVE_MEMORY,
    heading: 'Google Drive memory',
    description:
      'Content ingested from supported files in Google Drive, including sheets, docs, slides, PDFs, text files, CSVs, and JSON files. Use this when the source material is stored in Drive and Hana should read directly from those files.',
  },
  {
    type: MEMORY_BATCH_TYPES.PDF_MEMORY,
    heading: 'PDF memory',
    description:
      'A PDF ingested as memory. Use this for fixed-format documents such as brochures, reports, manuals, or reference packs where the PDF itself is the canonical artifact.',
  },
  {
    type: MEMORY_BATCH_TYPES.DOC_DOCX_MEMORY,
    heading: 'DOC or DOCX memory',
    description:
      'Microsoft Word documents ingested as memory. Use this when the source content is maintained as Word files rather than Google Docs or PDFs.',
  },
  {
    type: MEMORY_BATCH_TYPES.AUDIO_MEMORY,
    heading: 'Audio memory',
    description:
      'Audio files ingested into memory. Use this when the source knowledge is spoken and should be captured from recordings, conversations, or narrated material.',
  },
  {
    type: MEMORY_BATCH_TYPES.IMAGE_MEMORY,
    heading: 'Image memory',
    description:
      'Images ingested into memory. Use this when important knowledge is visual, such as diagrams, screenshots, brand references, or image-based instructions.',
  },
  {
    type: MEMORY_BATCH_TYPES.YOUTUBE_CAPTIONS_MEMORY,
    heading: 'YouTube captions memory',
    description:
      "A YouTube video's caption transcript ingested as memory. Use this when the value is in the spoken content of a video and the transcript is the right representation for retrieval.",
  },
  {
    type: MEMORY_BATCH_TYPES.CONFLUENCE_MEMORY,
    heading: 'Confluence memory',
    description:
      'Content ingested from Confluence pages. Use this for internal documentation, SOPs, runbooks, and wiki-style knowledge that lives in Confluence.',
  },
  {
    type: MEMORY_BATCH_TYPES.JIRA_MEMORY,
    heading: 'Jira memory',
    description:
      'Content ingested from Jira issues. Use this when tasks, issue history, or project execution context from Jira should be searchable and reusable as memory.',
  },
  {
    type: MEMORY_BATCH_TYPES.GOOGLE_CHAT_SPACE_MEMORY,
    heading: 'Google Chat space memory (Static)',
    description:
      'A one-time ingestion of message history from a Google Chat space. Use this when you want a snapshot of a conversation from a selected date range without continuously tracking new messages afterward.',
  },
  {
    type: MEMORY_BATCH_TYPES.GOOGLE_CHAT_SPACE_AUTO_TRACKING,
    heading: 'Google Chat space memory (Tracking)',
    description:
      'An ongoing tracked ingestion for a Google Chat space. Use this when Hana should continue pulling in new messages and evolving knowledge from that space over time.',
  },
  {
    type: MEMORY_BATCH_TYPES.GOOGLE_MEET_TRANSCRIPT_MEMORY,
    heading: 'Google Meet transcript memory',
    description:
      'A meeting transcript ingested as memory. Use this for decisions, discussion context, owners, and follow-ups captured in meeting transcripts.',
  },
  {
    type: MEMORY_BATCH_TYPES.GMAIL_INBOX_LABEL_MEMORY,
    heading: 'Gmail memory (Static)',
    description:
      'A one-time ingestion of emails from a Gmail label or selected mailbox scope. Use this when you need a bounded snapshot of email knowledge without ongoing updates.',
  },
  {
    type: MEMORY_BATCH_TYPES.GMAIL_INBOX_LABEL_AUTO_TRACKING,
    heading: 'Gmail memory (Tracking)',
    description:
      'An ongoing tracked ingestion of emails from Gmail. Use this when Hana should continue ingesting newly matching email threads over time.',
  },
];

export const MAX_DEPTH_FOR_RECURSIVE_URLS = 3; // Scroll a decent number of sub-pages

// Maximum number of sub pages that Hana will ingest per recursive URL memory
export const MAX_SUB_PAGES_FOR_RECURSIVE_URLS = 200;

export const PDF_FILE_SIZE_LIMIT = 250; // mb
export const DOC_FILE_SIZE_LIMIT = 50; // mb
export const IMAGE_FILE_SIZE_LIMIT = 15; // mb
export const IMAGE_FILE_SIZE_LIMIT_BYTES = IMAGE_FILE_SIZE_LIMIT * 1000 * 1000;

export const IMAGE_MEMORY_ACCEPTED_MIME_TYPES = [
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/webp',
  'image/gif',
  'image/heic',
  'image/heif',
];

export const IMAGE_FILE_SIZE_LIMIT_ERROR =
  'Selected image exceeds the maximum allowed size.';

export const MAX_PDF_PAGE_LIMIT = 1000; // pages

export const MEMORY_POLLING_INTERVAL = 15000; // in ms

export const IMAGE_MEMORY_DETAILS_SECTION_TITLE = 'Image Details';
export const IMAGE_MEMORY_SUMMARY_LABEL = 'Summary';
export const IMAGE_MEMORY_CAPTION_LABEL = 'Caption';
export const IMAGE_MEMORY_PRIMARY_SUBJECTS_LABEL = 'Primary Subjects';
export const IMAGE_MEMORY_DETECTED_TEXT_LABEL = 'Detected Text';
export const IMAGE_MEMORY_COLORS_LABEL = 'Dominant Colors';
export const IMAGE_MEMORY_USE_CASES_LABEL = 'Potential Use Cases';
export const IMAGE_MEMORY_SAFETY_FLAGS_LABEL = 'Safety Flags';
export const IMAGE_MEMORY_REFERENCE_URL_LABEL = 'Reference URL';
export const IMAGE_MEMORY_ORIGINAL_FILE_NAME_LABEL = 'Original File Name';
export const IMAGE_MEMORY_PREVIEW_ALT_TEXT = 'Uploaded image preview';
export const IMAGE_MEMORY_METADATA_EMPTY_VALUE = 'Not provided';
export const IMAGE_MEMORY_METADATA_LIST_DELIMITER = ', ';
export const IMAGE_MEMORY_ORIGINAL_REFERENCE_URL_LABEL =
  'Original Reference URL';

/**
 * Maximum number of memory batch ingestions a user can run concurrently.
 * Once this limit is reached new ingestion requests will be rejected.
 */
export const MAX_CONCURRENT_MEMORY_BATCH_INGESTIONS = 3;

export const MEMORY_BATCH_INGESTION_MAX_PROGRESS = 100;
// Mapping between memory tabs and their backing connectors.
// Used in `src/pages/memory/index.tsx` to gate tabs based on
// `CONNECTOR_WHITELISTED_DOMAINS`. This ensures domain-restricted
// connectors hide their associated memory tabs for unauthorized users.
export const MEMORY_TAB_CONNECTOR_MAP: Partial<
  Record<MEMORY_TABS, CONNECTOR_NAME>
> = {
  [MEMORY_TABS.confluenceMemoryList]: CONNECTOR_NAME.CONFLUENCE,
  [MEMORY_TABS.jiraMemoryList]: CONNECTOR_NAME.JIRA,
};

// Whitelist for memory tabs based on the user's email domain. Tabs not present
// here are open to all domains.
export const MEMORY_TAB_WHITELISTED_DOMAINS: Record<string, Array<string>> = {
  [MEMORY_TABS.generalMemoryList]: [],
  [MEMORY_TABS.erroredMemoryBatches]: [],
  [MEMORY_TABS.inProgressMemoryBatches]: [],
  [MEMORY_TABS.googleDocMemoryList]: [],
  [MEMORY_TABS.confluenceMemoryList]: [],
  [MEMORY_TABS.jiraMemoryList]: [],
  [MEMORY_TABS.recursiveUrlMemory]: [],
  [MEMORY_TABS.pdfMemory]: [],
  [MEMORY_TABS.docDocxMemory]: [],
  // Google Drive memory is open to all domains
  [MEMORY_TABS.googleDriveMemoryList]: [],
  // Audio memory is open to all domains
  [MEMORY_TABS.audioMemoryList]: [],
  [MEMORY_TABS.imageMemoryList]: [],
  [MEMORY_TABS.googleChatSpaceMemoryList]: [],
  // Open YouTube memory to all domains
  [MEMORY_TABS.youtubeCaptionsMemoryList]: [],
  [MEMORY_TABS.gmailMemoryList]: [],
};

// Product readiness gate: keep tabs hidden for all domains until launched.
// Deprecated tabs remain implemented for backward compatibility, but they
// should not be shown in the dashboard UI.
export const MEMORY_DISABLED_TABS: ReadonlyArray<MEMORY_TABS> = [
  MEMORY_TABS.confluenceMemoryList,
  MEMORY_TABS.jiraMemoryList,
  MEMORY_TABS.gmailMemoryList,
  MEMORY_TABS.imageMemoryList,
];

export const GOOGLE_CHAT_SPACE_ACTION_LABEL = 'Action';
export const GOOGLE_CHAT_SPACE_ACTION_FIELD_ID = 'googleChatSpaceAction';
export const GOOGLE_CHAT_SPACE_FIELD_ID = 'spaceId';
export const GOOGLE_CHAT_SPACE_LINK_LABEL = 'Google Chat space';
export const GOOGLE_CHAT_SPACE_LINK_HELPER_TEXT =
  'Select a space from the dropdown. Hana must already be added.';
export const GOOGLE_CHAT_SPACE_MULTI_SELECT_LABEL = 'Google Chat spaces';
export const GOOGLE_CHAT_SPACE_MULTI_SELECT_HELPER_TEXT =
  'Select one or more spaces. Hana must already be added to each selected space.';
export const GOOGLE_CHAT_SPACE_LINK_PLACEHOLDER = 'Start typing a space name';
export const GOOGLE_CHAT_SPACE_TRACK_OPTION_LABEL = 'Track space';
export const GOOGLE_CHAT_SPACE_INGEST_OPTION_LABEL = 'Ingest history';
export const GOOGLE_CHAT_SPACE_TRACK_OPTION_DESCRIPTION =
  'Hana will ingest new knowledge and messages from this space daily.';
export const GOOGLE_CHAT_SPACE_INGEST_OPTION_DESCRIPTION =
  'Hana will ingest messages from the selected date range only once as a static ingestion.';
export const GOOGLE_CHAT_SPACE_ACTION_HELPER_TEXT = `${GOOGLE_CHAT_SPACE_TRACK_OPTION_LABEL}: ${GOOGLE_CHAT_SPACE_TRACK_OPTION_DESCRIPTION} ${GOOGLE_CHAT_SPACE_INGEST_OPTION_LABEL}: ${GOOGLE_CHAT_SPACE_INGEST_OPTION_DESCRIPTION}`;

export const GOOGLE_CHAT_SPACE_START_DATE_LABEL = 'Start date';
export const GOOGLE_CHAT_SPACE_START_DATE_FIELD_ID = 'startDate';
export const GOOGLE_CHAT_SPACE_END_DATE_LABEL = 'End date';
export const GOOGLE_CHAT_SPACE_END_DATE_FIELD_ID = 'endDate';
export const START_DATE_REQUIRED_ERROR = 'Start date is required';
export const END_DATE_REQUIRED_ERROR = 'End date is required';
export const START_DATE_AFTER_END_DATE_ERROR =
  'Start date must not be after end date';
export const GOOGLE_CHAT_SPACE_TARGET_USER_BATCH_SUMMARY = (
  successCount: number,
  failureCount: number,
) =>
  failureCount > 0
    ? `Started ingestion for ${successCount} space(s). ${failureCount} space(s) could not be queued.`
    : `Started ingestion for ${successCount} space(s).`;
export const GOOGLE_CHAT_SPACE_TARGET_USER_FAILURE_PREFIX = 'Failed spaces:';

export const BATCH_TYPE_COLUMN_HEADER = 'Batch Type';
export const GOOGLE_CHAT_SPACE_BATCH_TYPE_LABELS: Record<string, string> = {
  [MEMORY_BATCH_TYPES.GOOGLE_CHAT_SPACE_MEMORY]: 'Static',
  [MEMORY_BATCH_TYPES.GOOGLE_CHAT_SPACE_AUTO_TRACKING]: 'Tracking',
};

export const GOOGLE_CHAT_SPACE_BATCH_TYPE_OPTIONS: ReadonlyArray<MEMORY_BATCH_TYPES> =
  [
    MEMORY_BATCH_TYPES.GOOGLE_CHAT_SPACE_MEMORY,
    MEMORY_BATCH_TYPES.GOOGLE_CHAT_SPACE_AUTO_TRACKING,
  ];

export const GMAIL_INBOX_LABEL_BATCH_TYPE_LABELS: Record<string, string> = {
  [MEMORY_BATCH_TYPES.GMAIL_INBOX_LABEL_MEMORY]: 'Static',
  [MEMORY_BATCH_TYPES.GMAIL_INBOX_LABEL_AUTO_TRACKING]: 'Tracking',
};

export const GMAIL_INBOX_LABEL_BATCH_TYPE_OPTIONS: ReadonlyArray<MEMORY_BATCH_TYPES> =
  [
    MEMORY_BATCH_TYPES.GMAIL_INBOX_LABEL_MEMORY,
    MEMORY_BATCH_TYPES.GMAIL_INBOX_LABEL_AUTO_TRACKING,
  ];

export const FILTER_ALL_LABEL = 'All';

export const MEMORY_BATCH_TYPE_I18N_KEYS: Record<
  keyof typeof MEMORY_BATCH_TYPES,
  string
> = {
  [MEMORY_BATCH_TYPES.TEXT_MEMORY]: 'general_memory',
  [MEMORY_BATCH_TYPES.RECURSIVE_URL_MEMORY]: 'recursive_url_memory',
  [MEMORY_BATCH_TYPES.GOOGLE_DOCS_MEMORY]: 'google_doc_memory',
  [MEMORY_BATCH_TYPES.GOOGLE_DRIVE_MEMORY]: 'google_drive_memory',
  [MEMORY_BATCH_TYPES.PDF_MEMORY]: 'pdf_memory',
  [MEMORY_BATCH_TYPES.DOC_DOCX_MEMORY]: 'doc_docx_memory',
  [MEMORY_BATCH_TYPES.AUDIO_MEMORY]: 'audio_memory',
  [MEMORY_BATCH_TYPES.IMAGE_MEMORY]: 'image_memory',
  [MEMORY_BATCH_TYPES.YOUTUBE_CAPTIONS_MEMORY]: 'youtube_memory',
  [MEMORY_BATCH_TYPES.CONFLUENCE_MEMORY]: 'confluence_memory',
  [MEMORY_BATCH_TYPES.JIRA_MEMORY]: 'jira_memory',
  [MEMORY_BATCH_TYPES.GOOGLE_CHAT_SPACE_MEMORY]: 'chat_space_memory',
  [MEMORY_BATCH_TYPES.GOOGLE_CHAT_SPACE_AUTO_TRACKING]: 'chat_space_memory',
  [MEMORY_BATCH_TYPES.GOOGLE_MEET_TRANSCRIPT_MEMORY]:
    'google_meet_transcript_memory',
  [MEMORY_BATCH_TYPES.GOOGLE_MEET_TRANSCRIPT_AUTO_TRACKING]:
    'google_meet_transcript_memory',
  [MEMORY_BATCH_TYPES.GOOGLE_TASKS_AUTO_TRACKING]: 'google_tasks_source',
  [MEMORY_BATCH_TYPES.GMAIL_INBOX_LABEL_MEMORY]: 'gmail_memory',
  [MEMORY_BATCH_TYPES.GMAIL_INBOX_LABEL_AUTO_TRACKING]: 'gmail_memory',
};

export const MEMORY_BATCH_TYPE_TO_TAB: Record<
  keyof typeof MEMORY_BATCH_TYPES,
  MEMORY_TABS
> = {
  [MEMORY_BATCH_TYPES.TEXT_MEMORY]: MEMORY_TABS.generalMemoryList,
  [MEMORY_BATCH_TYPES.RECURSIVE_URL_MEMORY]: MEMORY_TABS.recursiveUrlMemory,
  [MEMORY_BATCH_TYPES.GOOGLE_DOCS_MEMORY]: MEMORY_TABS.googleDocMemoryList,
  [MEMORY_BATCH_TYPES.GOOGLE_DRIVE_MEMORY]: MEMORY_TABS.googleDriveMemoryList,
  [MEMORY_BATCH_TYPES.PDF_MEMORY]: MEMORY_TABS.pdfMemory,
  [MEMORY_BATCH_TYPES.DOC_DOCX_MEMORY]: MEMORY_TABS.docDocxMemory,
  [MEMORY_BATCH_TYPES.AUDIO_MEMORY]: MEMORY_TABS.audioMemoryList,
  [MEMORY_BATCH_TYPES.IMAGE_MEMORY]: MEMORY_TABS.imageMemoryList,
  [MEMORY_BATCH_TYPES.YOUTUBE_CAPTIONS_MEMORY]:
    MEMORY_TABS.youtubeCaptionsMemoryList,
  [MEMORY_BATCH_TYPES.CONFLUENCE_MEMORY]: MEMORY_TABS.confluenceMemoryList,
  [MEMORY_BATCH_TYPES.JIRA_MEMORY]: MEMORY_TABS.jiraMemoryList,
  [MEMORY_BATCH_TYPES.GOOGLE_CHAT_SPACE_MEMORY]:
    MEMORY_TABS.googleChatSpaceMemoryList,
  [MEMORY_BATCH_TYPES.GOOGLE_CHAT_SPACE_AUTO_TRACKING]:
    MEMORY_TABS.googleChatSpaceMemoryList,
  [MEMORY_BATCH_TYPES.GOOGLE_MEET_TRANSCRIPT_MEMORY]:
    MEMORY_TABS.generalMemoryList,
  [MEMORY_BATCH_TYPES.GOOGLE_MEET_TRANSCRIPT_AUTO_TRACKING]:
    MEMORY_TABS.generalMemoryList,
  [MEMORY_BATCH_TYPES.GOOGLE_TASKS_AUTO_TRACKING]:
    MEMORY_TABS.generalMemoryList,
  [MEMORY_BATCH_TYPES.GMAIL_INBOX_LABEL_MEMORY]: MEMORY_TABS.gmailMemoryList,
  [MEMORY_BATCH_TYPES.GMAIL_INBOX_LABEL_AUTO_TRACKING]:
    MEMORY_TABS.gmailMemoryList,
};

export const MEMORY_BATCH_TYPE_OPTIONS: ReadonlyArray<MEMORY_BATCH_TYPES> = [
  MEMORY_BATCH_TYPES.TEXT_MEMORY,
  MEMORY_BATCH_TYPES.RECURSIVE_URL_MEMORY,
  MEMORY_BATCH_TYPES.GOOGLE_DOCS_MEMORY,
  MEMORY_BATCH_TYPES.GOOGLE_DRIVE_MEMORY,
  MEMORY_BATCH_TYPES.PDF_MEMORY,
  MEMORY_BATCH_TYPES.DOC_DOCX_MEMORY,
  MEMORY_BATCH_TYPES.AUDIO_MEMORY,
  MEMORY_BATCH_TYPES.IMAGE_MEMORY,
  MEMORY_BATCH_TYPES.YOUTUBE_CAPTIONS_MEMORY,
  MEMORY_BATCH_TYPES.CONFLUENCE_MEMORY,
  MEMORY_BATCH_TYPES.JIRA_MEMORY,
  MEMORY_BATCH_TYPES.GOOGLE_CHAT_SPACE_MEMORY,
  MEMORY_BATCH_TYPES.GOOGLE_CHAT_SPACE_AUTO_TRACKING,
  MEMORY_BATCH_TYPES.GMAIL_INBOX_LABEL_MEMORY,
  MEMORY_BATCH_TYPES.GMAIL_INBOX_LABEL_AUTO_TRACKING,
];

export const getVisibleMemoryBatchTypeOptions =
  (): ReadonlyArray<MEMORY_BATCH_TYPES> =>
    MEMORY_BATCH_TYPE_OPTIONS.filter((type) => {
      const tab = MEMORY_BATCH_TYPE_TO_TAB[type];

      return !MEMORY_DISABLED_TABS.includes(tab);
    });

export const getMemoryBatchTypesInfoMarkdown = (
  allowedTypes: ReadonlyArray<MEMORY_BATCH_TYPES> = getVisibleMemoryBatchTypeOptions(),
): string => {
  const sections = MEMORY_BATCH_TYPE_INFO_SECTIONS.filter(({ type }) =>
    allowedTypes.includes(type),
  );

  return `
## How to read memory batch types

Each memory batch type tells you what source Hana ingested and how that source should be interpreted during memory management. Picking the right type matters because the ingestion flow, refresh behavior, and maintenance expectations can differ by source.

${sections
  .map(
    ({ heading, description }) => `## ${heading}

${description}`,
  )
  .join('\n\n')}

## Static vs tracking modes

- Static batches are point-in-time ingestions. They capture the selected content once.
- Tracking batches are ongoing. They are meant for sources where new content keeps arriving and Hana should continue syncing it.

## Choosing the right type

- Choose the type that matches the real source of truth instead of copying content into a different format first.
- Prefer static ingestion when you need a controlled snapshot.
- Prefer tracking ingestion when the source changes regularly and Hana should stay current with it.
`;
};

export const GOOGLE_CHAT_SPACE_START_TRACKING_LABEL = 'Start Tracking';
export const GOOGLE_CHAT_SPACE_START_TRACKING_ID =
  'start-tracking-google-chat-space-memory';

export enum MEMORY_BATCH_RESYNC_FREQUENCY {
  ONE_DAY = 'ONE_DAY',
  THREE_DAYS = 'THREE_DAYS',
  ONE_WEEK = 'ONE_WEEK',
  ONE_MONTH = 'ONE_MONTH',
  THREE_MONTHS = 'THREE_MONTHS',
}

export const MEMORY_BATCH_RESYNC_SECONDS: Record<
  keyof typeof MEMORY_BATCH_RESYNC_FREQUENCY,
  number
> = {
  ONE_DAY: DAY_IN_SECONDS,
  THREE_DAYS: 3 * DAY_IN_SECONDS,
  ONE_WEEK: 7 * DAY_IN_SECONDS,
  ONE_MONTH: 30 * DAY_IN_SECONDS,
  THREE_MONTHS: 90 * DAY_IN_SECONDS,
};

export const MEMORY_BATCH_RESYNC_FREQUENCY_LABELS: Record<
  keyof typeof MEMORY_BATCH_RESYNC_FREQUENCY,
  string
> = {
  ONE_DAY: 'Every day',
  THREE_DAYS: 'Every 3 days',
  ONE_WEEK: 'Every week',
  ONE_MONTH: 'Every month',
  THREE_MONTHS: 'Every 3 months',
};

// Maximum number of memory batches that can have automatic resync
// enabled for a single organization. Prevents abusive background load.
export const MAX_AUTO_RESYNC_ENABLED_BATCHES = 60;

export const AUTO_RESYNC_INGESTION_STATUS_BYPASS_BATCH_TYPES: ReadonlyArray<MEMORY_BATCH_TYPES> =
  [
    MEMORY_BATCH_TYPES.GOOGLE_CHAT_SPACE_AUTO_TRACKING,
    MEMORY_BATCH_TYPES.GMAIL_INBOX_LABEL_AUTO_TRACKING,
  ];

export const GMAIL_MEMORY_CREATE_BUTTON_TEXT = 'Add Gmail memory';
export const GMAIL_MEMORY_ACTION_LABEL = 'Ingestion type';
export const GMAIL_MEMORY_ACTION_FIELD_ID = 'gmailMemoryAction';
export const GMAIL_MEMORY_TRACK_OPTION_LABEL = 'Track inbox';
export const GMAIL_MEMORY_INGEST_OPTION_LABEL = 'Ingest once';
export const GMAIL_MEMORY_TRACK_OPTION_DESCRIPTION =
  'Hana will sync new emails matching these filters daily.';
export const GMAIL_MEMORY_INGEST_OPTION_DESCRIPTION =
  'Hana will ingest matching emails once as a static ingestion.';
export const GMAIL_MEMORY_ACTION_HELPER_TEXT = `${GMAIL_MEMORY_TRACK_OPTION_LABEL}: ${GMAIL_MEMORY_TRACK_OPTION_DESCRIPTION} ${GMAIL_MEMORY_INGEST_OPTION_LABEL}: ${GMAIL_MEMORY_INGEST_OPTION_DESCRIPTION}`;
export const GMAIL_MEMORY_QUERY_FIELD_ID = 'gmailQuery';
export const GMAIL_MEMORY_QUERY_LABEL = 'Gmail search query';
export const GMAIL_MEMORY_QUERY_PLACEHOLDER =
  'from:client newer_than:14d has:attachment';
export const GMAIL_MEMORY_QUERY_HELPER_TEXT =
  'Use Gmail web search syntax. Either a query or at least one label is required.';
export const GMAIL_MEMORY_FILTER_REQUIRED_ERROR =
  'gmailQuery or gmailLabelIds is required';

export const GMAIL_MEMORY_LABEL_IDS_FIELD_ID = 'gmailLabelIds';
export const GMAIL_MEMORY_LABEL_IDS_LABEL = 'Gmail labels';
export const GMAIL_MEMORY_LABEL_IDS_HELPER_TEXT =
  'Optional. Filters messages that have all selected labels.';

export const GMAIL_MEMORY_MAX_RESULTS_FIELD_ID = 'gmailMaxResults';
export const GMAIL_MEMORY_MAX_RESULTS_LABEL = 'Max messages (initial sync)';
export const GMAIL_MEMORY_MAX_RESULTS_LIMIT = 2000;
export const GMAIL_MEMORY_MAX_RESULTS_HELPER_TEXT = `Optional. Must be a positive integer up to ${GMAIL_MEMORY_MAX_RESULTS_LIMIT}.`;

export const GMAIL_MEMORY_INCLUDE_ATTACHMENTS_FIELD_ID =
  'gmailIncludeAttachments';
export const GMAIL_MEMORY_INCLUDE_ATTACHMENTS_LABEL =
  'Include small text attachments';
export const GMAIL_MEMORY_INCLUDE_ATTACHMENTS_HELPER_TEXT =
  'When enabled, Hana will fetch and summarize small text attachments (when available).';

export const GMAIL_MEMORY_SUPER_ADMIN_CREATE_NOT_SUPPORTED =
  'Creating Gmail memories as Super Admin is not supported yet. Clear the target user and try again.';

export enum SUB_URL_INGESTION_STATUS {
  ENABLED = 'Enabled',
  DISABLED = 'Disabled',
}

export const SUB_URL_INGESTION_STATUS_COLORS: Record<
  SUB_URL_INGESTION_STATUS,
  'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'
> = {
  [SUB_URL_INGESTION_STATUS.ENABLED]: 'success',
  [SUB_URL_INGESTION_STATUS.DISABLED]: 'default',
};

export const SUB_URL_INGESTION_HEADER_NAME = 'Sub URL Ingestion';

export const NEXT_RESYNC_AT_HEADER_NAME = 'Next Resync At';
export const AUTO_RESYNC_ENABLED_HEADER_NAME = 'Auto Resync Enabled';
export const AUTO_RESYNC_FREQUENCY_HEADER_NAME = 'Auto Resync Frequency';
export const AUTO_RESYNC_TASK_NAME_HEADER_NAME = 'Auto Resync Task Name';
export const AUTO_RESYNC_FAILURE_COUNT_HEADER_NAME =
  'Auto Resync Failure Count';
export const REMAINING_CHUNK_TASKS_HEADER_NAME = 'Remaining Chunk Tasks';
export const MEMORY_BOOLEAN_TRUE_LABEL = 'Yes';
export const MEMORY_BOOLEAN_FALSE_LABEL = 'No';
export const AUTO_RESYNC_ENABLED_COLUMN_WIDTH = 160;
export const AUTO_RESYNC_FREQUENCY_COLUMN_WIDTH = 180;
export const AUTO_RESYNC_TASK_NAME_COLUMN_WIDTH = 260;
export const AUTO_RESYNC_FAILURE_COUNT_COLUMN_WIDTH = 200;
export const REMAINING_CHUNK_TASKS_COLUMN_WIDTH = 220;
export const MEMORY_TABLE_PLACEHOLDER = '-';

export const LOAD_CHUNKS_TOGETHER_FIELD_ID = 'loadChunksTogether';
export const LOAD_CHUNKS_TOGETHER_LABEL =
  'Load memory as a whole always (by default the memory is broken down into smaller chunks)';
export const LOAD_CHUNKS_TOGETHER_WARNING =
  'This will include the entire memory for every request which may increase context size. Enable only when completeness is required. This cannot be changed later on.';

export const INCLUDE_TIMESTAMPS_FIELD_ID = 'includeTimestamps';
export const INCLUDE_TIMESTAMPS_LABEL = 'Include segment timestamps';
export const INCLUDE_TIMESTAMPS_HELPER =
  'Store start and duration for each audio segment in the memory.';

export const MEMORY_DESCRIPTION_FIELD_ID = 'memoryDescription';
export const MEMORY_CONTEXT_DESCRIPTION_LABEL = 'Memory Context Description';

export const CRAWL_SUB_PAGES_PRO_PLAN_MESSAGE =
  'Crawling multiple sub pages is available only on the Pro plan.';

export const ADVANCED_CRAWLER_PRO_PLAN_MESSAGE =
  'Advanced crawler and crawl options are available only on the Pro plan.';

export const TARGET_USER_WARNING_TITLE = 'Acting On Behalf Of Another User';
export const TARGET_USER_WARNING_TEXT =
  'All actions will be executed for the target user. Proceed with caution.';
export const CLEAR_TARGET_USER_BUTTON_TEXT = 'Clear Target User';

export const COPY_USER_ID_LABEL = 'Copy ID';

export const ERROR_BATCH_CALLOUT_MESSAGE = `Some memory batches failed to ingest. We can manage them for you so you can focus on using ${BOT_NAME}. Please email ${SUPPORT_EMAIL} for help.`;

export const MEMORY_CHUNKS_PER_PAGE = 9;

export const ERRORED_BATCHES_TABLE_TITLE = 'Errored Memory Batches';
export const ERRORED_BATCHES_TABLE_DESCRIPTION =
  'Batches listed here failed to ingest. Review them to unblock users quickly.';
export const IN_PROGRESS_BATCHES_TABLE_TITLE = 'In-Progress Memory Batches';
export const IN_PROGRESS_BATCHES_TABLE_DESCRIPTION =
  'Batches listed here are actively ingesting across all organizations. Review live progress and jump straight to the source batch when needed.';
export const ERRORED_BATCHES_SEARCH_PLACEHOLDER =
  'Search by batch title, creator, or ID';
export const ERRORED_BATCHES_ORG_PLACEHOLDER = 'Organization ID';
export const ERRORED_BATCHES_RESET_FILTERS_LABEL = 'Reset Filters';
export const ERRORED_BATCHES_EMPTY_STATE_MESSAGE = 'No errored batches found.';
export const IN_PROGRESS_BATCHES_EMPTY_STATE_MESSAGE =
  'No in-progress batches found.';
export const ERRORED_BATCHES_TABLE_ARIA_LABEL = 'Errored memory batches table';
export const IN_PROGRESS_BATCHES_TABLE_ARIA_LABEL =
  'In-progress memory batches table';
export const ERRORED_BATCHES_VIEW_DETAILS_LABEL = 'Open in Memory Module';
export const ERRORED_BATCHES_VIEW_CHUNKS_LABEL = 'Open Details';
export const ERRORED_BATCHES_RETRY_BUTTON_LABEL = 'Retry Ingestion';
export const ERRORED_BATCHES_ACTIONS_COLUMN_HEADER = 'Actions';
export const ERRORED_BATCHES_INGESTION_STATUS_HEADER = 'Ingestion Status';
export const ERRORED_BATCHES_MEMORY_LEVEL_HEADER = 'Memory Level';
export const ERRORED_BATCHES_CREATED_AT_HEADER = 'Created At';
export const ERRORED_BATCHES_ORGANIZATION_HEADER = 'Organization';
export const ERRORED_BATCHES_BATCH_TITLE_HEADER = 'Memory Batch';
export const ERRORED_BATCHES_CREATED_BY_HEADER = 'Created By';
export const ERRORED_BATCHES_RETRY_SUCCESS_MESSAGE =
  'Ingestion retry started successfully.';
export const ERRORED_BATCHES_NO_CREATOR_MESSAGE =
  'Cannot perform this action because the batch creator is unavailable.';
export const ERRORED_BATCHES_ROW_HEIGHT = 52;
export const ERRORED_BATCHES_ACTIONS_COLUMN_WIDTH = 220;
export const ERRORED_BATCHES_ORG_COLUMN_WIDTH = 240;
export const ERRORED_BATCHES_BATCH_TITLE_COLUMN_WIDTH = 280;
export const ERRORED_BATCHES_FETCH_ERROR_MESSAGE =
  'Unable to load errored batches.';
export const ERRORED_BATCHES_FILTER_MIN_WIDTH = '200px';

export const CHUNK_BATCH_ID_TEXT_FIELD_DESKTOP_WIDTH = '320px';
export const MEMORY_CHUNKS_MODULE_TITLE = 'Memory Chunks';
export const CHUNK_METADATA_LABEL = 'Metadata';
export const CHUNK_TEXT_LABEL = 'Text';
export const CHUNK_METADATA_UNAVAILABLE_MESSAGE = 'No metadata available';

export const MEMORY_PAGE_DATA_PER_PAGE = 10;
export const MEMORY_PAGE_DATA_MODULE_TITLE = 'Sheet Data';
export const PAGE_DATA_TEXT_LABEL = 'Row Content';
export const PAGE_DATA_SHEET_TITLE_LABEL = 'Sheet Title';
export const PAGE_DATA_ROW_INDEX_LABEL = 'Row Number';
export const PAGE_DATA_UNAVAILABLE_MESSAGE = 'No sheet data available';
export const VIEW_PAGE_DATA_LABEL = 'View Sheet Data';
export const VISIT_LABEL = 'Visit';
export const VISIT_ID = 'visit-google-drive-sheet';
export const SYNC_LABEL = 'Sync';
export const SYNC_YOUTUBE_MEMORY_ID = 'sync-youtube-memory';

// Maximum length allowed for batch use case instructions.
export const MAX_USE_CASE_INSTRUCTIONS_LENGTH = 2000;

export const USE_CASE_INSTRUCTIONS_FIELD_ID = 'useCaseInstructions';
export const USE_CASE_INSTRUCTIONS_LABEL = 'Use case instructions (optional)';

export const USE_CASE_INSTRUCTIONS_PLACEHOLDERS: Record<
  keyof typeof MEMORY_BATCH_TYPES,
  string
> = {
  [MEMORY_BATCH_TYPES.TEXT_MEMORY]:
    'This represents the birthday dates of people at Hanabi',
  [MEMORY_BATCH_TYPES.RECURSIVE_URL_MEMORY]:
    'Use this webpage when asked about past projects of our company',
  [MEMORY_BATCH_TYPES.GOOGLE_DOCS_MEMORY]:
    'Use this document for employee schedules',
  [MEMORY_BATCH_TYPES.GOOGLE_DRIVE_MEMORY]:
    'Use this document for employee schedules data. Each row represents data for each employee. Columns are - Employee name, shift timings, shift days, payroll information, employee id, manager name.',
  [MEMORY_BATCH_TYPES.PDF_MEMORY]:
    'This document is the company sales brochure. It has information about our services and past projects.',
  [MEMORY_BATCH_TYPES.DOC_DOCX_MEMORY]:
    'Use this document for company leave policy.',
  [MEMORY_BATCH_TYPES.AUDIO_MEMORY]: 'Use this audio for employee schedules',
  [MEMORY_BATCH_TYPES.IMAGE_MEMORY]:
    'Use this image for marketing visuals and brand guidelines',
  [MEMORY_BATCH_TYPES.YOUTUBE_CAPTIONS_MEMORY]:
    'Use this video for comparison of Langchain and Langgraph',
  [MEMORY_BATCH_TYPES.CONFLUENCE_MEMORY]:
    'Use this page for release SOP for Hana server',
  [MEMORY_BATCH_TYPES.JIRA_MEMORY]:
    'Use this issue for task updates on Hana project',
  [MEMORY_BATCH_TYPES.GOOGLE_CHAT_SPACE_MEMORY]:
    'Use this conversation for Hana project development and what is currently occuring at a high level in Hana',
  [MEMORY_BATCH_TYPES.GOOGLE_CHAT_SPACE_AUTO_TRACKING]:
    'Use this conversation for Hana project development and what is currently occuring at a high level in Hana',
  [MEMORY_BATCH_TYPES.GOOGLE_MEET_TRANSCRIPT_MEMORY]:
    'Use this meeting transcript for decisions, owners, and follow-ups discussed.',
  [MEMORY_BATCH_TYPES.GOOGLE_MEET_TRANSCRIPT_AUTO_TRACKING]:
    'Use tracked meeting transcripts for decisions, owners, and follow-ups discussed.',
  [MEMORY_BATCH_TYPES.GOOGLE_TASKS_AUTO_TRACKING]:
    'Read-only external action source. Not used for answer knowledge.',
  [MEMORY_BATCH_TYPES.GMAIL_INBOX_LABEL_MEMORY]:
    'Use these emails for tracking decisions, deadlines, and action items from important threads.',
  [MEMORY_BATCH_TYPES.GMAIL_INBOX_LABEL_AUTO_TRACKING]:
    'Use these emails for tracking decisions, deadlines, and action items from important threads.',
};

// Google Sheets ingestion configuration
// Keep the dashboard contract aligned with the server. This default is used
// only by the bounded TOP_ROWS and BOTTOM_ROWS strategies; ALL_ROWS is
// intentionally lossless and schedules the entire selected source.
export const GOOGLE_SHEETS_TOTAL_ROW_LIMIT_DEFAULT = GOOGLE_SHEETS_ROW_LIMIT;
export const GOOGLE_SHEETS_TOTAL_ROW_MIN_LIMIT = 1;

export enum SHEET_INGESTION_STRATEGY {
  ALL_ROWS = 'ALL_ROWS',
  TOP_ROWS = 'TOP_ROWS',
  BOTTOM_ROWS = 'BOTTOM_ROWS',
}

export const SHEET_INGESTION_STRATEGY_FIELD_ID = 'sheetIngestionStrategy';
export const SHEET_INGESTION_STRATEGY_LABEL = 'Rows to ingest';
export const SHEET_INGESTION_ALL_ROWS_DESCRIPTION =
  'Ingest every row. Large sheets are split into bounded ingestion jobs so later rows are not silently dropped.';
export const SHEET_INGESTION_TOP_ROWS_DESCRIPTION = `Ingest only the first, max ${GOOGLE_SHEETS_ROW_LIMIT} rows (top of the sheet).`;
export const SHEET_INGESTION_BOTTOM_ROWS_DESCRIPTION = `Ingest only the last, max ${GOOGLE_SHEETS_ROW_LIMIT} rows (bottom of the sheet).`;
export const SHEET_INGESTION_STRATEGY_OPTIONS = [
  {
    value: SHEET_INGESTION_STRATEGY.ALL_ROWS,
    label: 'All rows',
    description: SHEET_INGESTION_ALL_ROWS_DESCRIPTION,
  },
  {
    value: SHEET_INGESTION_STRATEGY.TOP_ROWS,
    label: 'Top rows',
    description: SHEET_INGESTION_TOP_ROWS_DESCRIPTION,
  },
  {
    value: SHEET_INGESTION_STRATEGY.BOTTOM_ROWS,
    label: 'Bottom rows',
    description: SHEET_INGESTION_BOTTOM_ROWS_DESCRIPTION,
  },
];

export const SHEET_TITLE_FIELD_ID = 'sheetTitle';
export const SHEET_TITLE_LABEL =
  'Sheet title (ingest the sheet tab with this exact title ONLY)';
export const SHEET_TITLE_ALL_SHEETS_OPTION_VALUE = '__ALL_SHEETS__';
export const SHEET_TITLE_ALL_SHEETS_OPTION_LABEL = 'All sheets';

export const SHEET_TOTAL_ROW_LIMIT_FIELD_ID = 'sheetTotalRowLimit';
export const SHEET_TOTAL_ROW_LIMIT_LABEL = 'Total row limit';
export const SHEET_TOTAL_ROW_LIMIT_HELPER_TEXT = `Applies only to Top rows and Bottom rows. Leave blank to use ${GOOGLE_SHEETS_TOTAL_ROW_LIMIT_DEFAULT} rows across selected sheets; maximum ${GOOGLE_SHEETS_ROW_LIMIT}. All rows always ingests the complete selected source.`;
export const SHEET_TOTAL_ROW_LIMIT_NUMBER_ERROR = 'Row limit must be a number.';
export const SHEET_TOTAL_ROW_LIMIT_MIN_ERROR = `Row limit must be at least ${GOOGLE_SHEETS_TOTAL_ROW_MIN_LIMIT}.`;
export const SHEET_TOTAL_ROW_LIMIT_MAX_ERROR = `Row limit cannot exceed ${GOOGLE_SHEETS_ROW_LIMIT}.`;
export const SHEET_TOTAL_ROW_LIMIT_HEADER_NAME = 'Row cap';
export const EDIT_SHEET_OPTIONS_LABEL = 'Edit Sheet Options';
export const EDIT_SHEET_OPTIONS_ADMIN_LABEL = 'Edit Sheet Options (Admin)';
export const EDIT_SHEET_OPTIONS_AS_USER_LABEL = 'Edit Sheet Options as User…';
export const SHEET_OPTIONS_USER_ID_FIELD_ID = 'sheetOptionsUserId';
export const SHEET_OPTIONS_USER_ID_LABEL = 'User ID';

export enum SHEET_SORT_TYPE {
  ALPHABETICAL = 'ALPHABETICAL',
  DATE = 'DATE',
  NUMERIC = 'NUMERIC',
}

export enum SHEET_SORT_ORDER {
  ASC = 'ASC',
  DESC = 'DESC',
}

export const SHEET_SORT_COLUMN_FIELD_ID = 'sheetSortColumnIndex';
export const SHEET_SORT_COLUMN_LABEL = 'Sort column index';
export const SHEET_SORT_COLUMN_HELPER_TEXT = '0-based index';

export const SHEET_SORT_TYPE_FIELD_ID = 'sheetSortType';
export const SHEET_SORT_TYPE_LABEL = 'Sort type';
export const SHEET_SORT_TYPE_ALPHABETICAL_DESCRIPTION =
  'Sorts text values (A→Z or Z→A).';
export const SHEET_SORT_TYPE_DATE_DESCRIPTION =
  'Sorts by date/time values (oldest→newest or newest→oldest).';
export const SHEET_SORT_TYPE_NUMERIC_DESCRIPTION =
  'Sorts numeric values (smallest→largest or largest→smallest).';
export const SHEET_SORT_TYPE_OPTIONS = [
  {
    value: SHEET_SORT_TYPE.ALPHABETICAL,
    label: 'Alphabetical',
    description: SHEET_SORT_TYPE_ALPHABETICAL_DESCRIPTION,
  },
  {
    value: SHEET_SORT_TYPE.DATE,
    label: 'Date',
    description: SHEET_SORT_TYPE_DATE_DESCRIPTION,
  },
  {
    value: SHEET_SORT_TYPE.NUMERIC,
    label: 'Numeric',
    description: SHEET_SORT_TYPE_NUMERIC_DESCRIPTION,
  },
];

export const SHEET_SORT_ORDER_FIELD_ID = 'sheetSortOrder';
export const SHEET_SORT_ORDER_LABEL = 'Sort order';
export const SHEET_SORT_ORDER_ASC_DESCRIPTION =
  'A→Z, oldest→newest, or smallest→largest.';
export const SHEET_SORT_ORDER_DESC_DESCRIPTION =
  'Z→A, newest→oldest, or largest→smallest.';
export const SHEET_SORT_ORDER_OPTIONS = [
  {
    value: SHEET_SORT_ORDER.ASC,
    label: 'Ascending',
    description: SHEET_SORT_ORDER_ASC_DESCRIPTION,
  },
  {
    value: SHEET_SORT_ORDER.DESC,
    label: 'Descending',
    description: SHEET_SORT_ORDER_DESC_DESCRIPTION,
  },
];

export const SHEET_SORT_OPTIONS_HEADING = 'Sorting options';

export const SHEET_PREVIEW_MAX_ROWS = 10;
export const SHEET_PREVIEW_MAX_COLUMNS = 30;
export const SHEET_PREVIEW_ROWS_LABEL = 'Non-empty rows';
export const SHEET_PREVIEW_TABLE_ARIA_LABEL = 'Sheet preview';
export const SHEET_NO_DATA_WARNING = 'Selected sheet has no non-empty rows.';
export const SHEET_ANALYSIS_PREVIEW_LIMIT_WARNING = `Preview shows only the first ${SHEET_PREVIEW_MAX_ROWS} rows and ${SHEET_PREVIEW_MAX_COLUMNS} columns. Some data may be omitted.`;
